import { TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import type { InputType } from 'config/input.type.constants'
import { Button } from 'stories/components'
import { RenderInput } from 'utils/RenderInput'

import { IButtonType, IEmailButton } from './type'

export interface EmailLinksEditorProperties {
  nonRemovalLink?: string
  showError?: boolean
}

export interface EmailLinksEditorProps {
  value: IEmailButton[]
  onChange: (links: IEmailButton[]) => void
  onBlur?: () => void
  error?: string
  properties?: EmailLinksEditorProperties
  span?: number
}

export function EmailLinksEditor({ value, onChange, onBlur, error, properties, span }: EmailLinksEditorProps) {
  const links = value ?? []
  const { nonRemovalLink, showError } = properties ?? {}

  const updateLink = (key: string, fieldValue: any, index: number) => {
    const next = cloneDeep(links)
    ;(next as any)[index][key] = fieldValue
    onChange(next)
  }

  const removeLink = (index: number) => {
    const next = cloneDeep(links)
    if (next.length) next.splice(index, 1)
    onChange(next)
  }

  const addLink = () => {
    const next = cloneDeep(links)
    next.push({ label: '', url: '', type: 'button' })
    onChange(next)
  }

  return (
    <>
      {links.map((link: Record<string, any>, index: number) => {
        const input: InputType = {
          inputType: 'text',
          title: '',
          value: link.url,
          prefix: 'Link:',
          indent: 12,
          showLinkDir: true,
        }

        input.additionalElements = (
          <>
            <input
              className="border-gray-300 border-[1px] px-1"
              value={link.label}
              onChange={(e: any) => updateLink('label', e.target.value, index)}
              onBlur={onBlur}
              placeholder={`${IButtonType[link.type]} Label`}
            />
            <select
              name={`button-type-${index}`}
              id={`button-type-${index}`}
              className="py-0 pl-1 pr-6 text-[12.5px] leading-[16px] border-gray-300"
              value={link.type}
              onChange={(e) => updateLink('type', e.target.value, index)}
              onBlur={onBlur}
            >
              {Object.keys(IButtonType).map((key) => (
                <option value={key} key={key}>
                  {IButtonType[key]}
                </option>
              ))}
            </select>
            {(!nonRemovalLink || link.url !== nonRemovalLink) && (
              <span className="cursor-pointer text-red-800" onClick={() => removeLink(index)}>
                <TrashIcon className="w-4 h-4" />
              </span>
            )}
          </>
        )

        if (showError && !(link.label && link.url)) {
          input.error = `${link.label ? '' : 'Label is required.'} ${link.url ? '' : 'Link is required.'}`
        }

        return (
          <div className={`col-span-${span ?? input.span ?? 1} opacity-100`} key={index}>
            <RenderInput
              input={input}
              Key={`link-${index}`}
              key={`link-${index}`}
              onChange={(_key: string, v: any) => updateLink('url', v, index)}
              onBlur={onBlur}
            />
          </div>
        )
      })}
      <div className="col-span-full">
        <Button link onClick={addLink}>
          + Add Button &amp; Link
        </Button>
        {error ? <p className="text-rose-700 text-[13px] pt-1 pl-1">{error}</p> : null}
      </div>
    </>
  )
}
